import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  BarChart3,
  Users,
  Eye,
  TrendingUp,
  Calendar,
  MessageSquare,
  Briefcase,
  UserCheck,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

interface AnalyticsData {
  totalConsultants: number;
  totalClients: number;
  totalJobPostings: number;
  totalMessages: number;
  totalAccessRequests: number;
  totalUserRequests: number;
  recentSignups: number;
  activeJobs: number;
}

interface ProfileCompletionBucket {
  at80OrLess: number;
  at90: number;
  at100: number;
}

export default function WebsiteAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalConsultants: 0,
    totalClients: 0,
    totalJobPostings: 0,
    totalMessages: 0,
    totalAccessRequests: 0,
    totalUserRequests: 0,
    recentSignups: 0,
    activeJobs: 0
  });
  const [profileCompletion, setProfileCompletion] = useState<ProfileCompletionBucket>({
    at80OrLess: 0,
    at90: 0,
    at100: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchAnalytics();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const [
        consultants,
        clients,
        jobPostings,
        messages,
        accessRequests,
        userRequests,
        recentConsultants,
        activeJobs
      ] = await Promise.all([
        supabase.from('consultant_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('workday_client_profiles').select('*', { count: 'exact', head: true }),
        supabase.from('workday_client_job_postings').select('*', { count: 'exact', head: true }),
        supabase.from('contacts').select('*', { count: 'exact', head: true }),
        supabase.from('access_requests').select('*', { count: 'exact', head: true }),
        supabase.from('user_account_requests').select('*', { count: 'exact', head: true }),
        supabase
          .from('consultant_profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', thirtyDaysAgo.toISOString()),
        supabase
          .from('workday_client_job_postings')
          .select('*', { count: 'exact', head: true })
          .eq('is_draft', false)
      ]);

      setAnalytics({
        totalConsultants: consultants.count || 0,
        totalClients: clients.count || 0,
        totalJobPostings: jobPostings.count || 0,
        totalMessages: messages.count || 0,
        totalAccessRequests: accessRequests.count || 0,
        totalUserRequests: userRequests.count || 0,
        recentSignups: recentConsultants.count || 0,
        activeJobs: activeJobs.count || 0
      });

      await fetchProfileCompletionStats();
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const COMPLETION_FIELDS = [
    'first_name', 'last_name', 'email', 'title',
    'country_region', 'experience_years', 'workday_modules',
    'resume_url', 'photo_url', 'experience_text'
  ] as const;

  const computeCompleteness = (row: any): number => {
    const filled = COMPLETION_FIELDS.filter(f => row[f] && row[f].toString().trim() !== '').length;
    return Math.round((filled / COMPLETION_FIELDS.length) * 100);
  };

  const fetchProfileCompletionStats = async () => {
    try {
      const { data } = await supabase
        .from('consultant_profiles')
        .select('first_name, last_name, email, title, country_region, experience_years, workday_modules, resume_url, photo_url, experience_text')
        .is('soft_deleted_at', null)
        .or('is_test_account.is.null,is_test_account.eq.false');

      if (!data) return;

      let at80OrLess = 0;
      let at90 = 0;
      let at100 = 0;

      for (const row of data) {
        const pct = computeCompleteness(row);
        if (pct === 100) at100++;
        else if (pct >= 90) at90++;
        else at80OrLess++;
      }

      setProfileCompletion({ at80OrLess, at90, at100 });
    } catch (err) {
      console.error('Error fetching profile completion stats:', err);
    }
  };

  const stats = [
    {
      name: 'Total Consultants',
      value: analytics.totalConsultants,
      icon: UserCheck,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      name: 'Total Clients',
      value: analytics.totalClients,
      icon: Briefcase,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      name: 'Active Job Postings',
      value: analytics.activeJobs,
      icon: TrendingUp,
      color: 'bg-[#40E0D0]',
      bgColor: 'bg-[#40E0D0]/10',
      textColor: 'text-[#40E0D0]'
    },
    {
      name: 'Total Messages',
      value: analytics.totalMessages,
      icon: MessageSquare,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      name: 'Recent Signups (30d)',
      value: analytics.recentSignups,
      icon: Calendar,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    },
    {
      name: 'Access Requests',
      value: analytics.totalAccessRequests,
      icon: Eye,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-600'
    },
    {
      name: 'User Account Requests',
      value: analytics.totalUserRequests,
      icon: Users,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600'
    },
    {
      name: 'Total Job Postings',
      value: analytics.totalJobPostings,
      icon: BarChart3,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    }
  ];

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Website Analytics</h1>
            <p className="text-gray-600">Overview of key metrics and statistics</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#40E0D0]"></div>
              <p className="mt-4 text-gray-600">Loading analytics...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat) => (
                  <div
                    key={stat.name}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-center justify-between">
                      <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                        <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#40E0D0]" />
                    Growth Overview
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Consultant Growth</span>
                        <span className="text-sm font-medium text-gray-900">
                          {analytics.totalConsultants} total
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{
                            width: `${Math.min((analytics.recentSignups / analytics.totalConsultants) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {analytics.recentSignups} new in the last 30 days
                      </p>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Job Posting Activity</span>
                        <span className="text-sm font-medium text-gray-900">
                          {analytics.activeJobs} active
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#40E0D0] h-2 rounded-full"
                          style={{
                            width: `${Math.min((analytics.activeJobs / Math.max(analytics.totalJobPostings, 1)) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {analytics.totalJobPostings} total postings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#40E0D0]" />
                    Platform Engagement
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Consultant-to-Client Ratio</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {analytics.totalClients > 0
                            ? (analytics.totalConsultants / analytics.totalClients).toFixed(1)
                            : '0'}
                          :1
                        </p>
                      </div>
                      <UserCheck className="h-8 w-8 text-[#40E0D0]" />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Avg Jobs per Client</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {analytics.totalClients > 0
                            ? (analytics.totalJobPostings / analytics.totalClients).toFixed(1)
                            : '0'}
                        </p>
                      </div>
                      <Briefcase className="h-8 w-8 text-[#40E0D0]" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Consultant Profile Completion
                </h2>
                <p className="text-sm text-gray-500 mb-5">
                  Consultants must reach 90% or 100% for clients to request interviews. Those at 80% or below need attention.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-red-700">80% or Below</span>
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    </div>
                    <p className="text-3xl font-bold text-red-700">{profileCompletion.at80OrLess}</p>
                    <p className="text-sm text-red-600 mt-1">
                      {analytics.totalConsultants > 0
                        ? `${Math.round((profileCompletion.at80OrLess / analytics.totalConsultants) * 100)}% of total`
                        : '0% of total'}
                    </p>
                    <div className="mt-3 w-full bg-red-200 rounded-full h-1.5">
                      <div
                        className="bg-red-500 h-1.5 rounded-full"
                        style={{ width: analytics.totalConsultants > 0 ? `${Math.min((profileCompletion.at80OrLess / analytics.totalConsultants) * 100, 100)}%` : '0%' }}
                      />
                    </div>
                    <p className="text-xs text-red-500 mt-1.5">Incomplete — not interview-eligible</p>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-amber-700">90% Complete</span>
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    </div>
                    <p className="text-3xl font-bold text-amber-700">{profileCompletion.at90}</p>
                    <p className="text-sm text-amber-600 mt-1">
                      {analytics.totalConsultants > 0
                        ? `${Math.round((profileCompletion.at90 / analytics.totalConsultants) * 100)}% of total`
                        : '0% of total'}
                    </p>
                    <div className="mt-3 w-full bg-amber-200 rounded-full h-1.5">
                      <div
                        className="bg-amber-500 h-1.5 rounded-full"
                        style={{ width: analytics.totalConsultants > 0 ? `${Math.min((profileCompletion.at90 / analytics.totalConsultants) * 100, 100)}%` : '0%' }}
                      />
                    </div>
                    <p className="text-xs text-amber-500 mt-1.5">Interview-eligible at 90%+</p>
                  </div>

                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-emerald-700">100% Complete</span>
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    </div>
                    <p className="text-3xl font-bold text-emerald-700">{profileCompletion.at100}</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      {analytics.totalConsultants > 0
                        ? `${Math.round((profileCompletion.at100 / analytics.totalConsultants) * 100)}% of total`
                        : '0% of total'}
                    </p>
                    <div className="mt-3 w-full bg-emerald-200 rounded-full h-1.5">
                      <div
                        className="bg-emerald-500 h-1.5 rounded-full"
                        style={{ width: analytics.totalConsultants > 0 ? `${Math.min((profileCompletion.at100 / analytics.totalConsultants) * 100, 100)}%` : '0%' }}
                      />
                    </div>
                    <p className="text-xs text-emerald-500 mt-1.5">Fully complete — interview-eligible</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
